// Everything about solving a puzzle that is pure: where the cursor goes,
// which cells make up the current word, whether the grid is solved. The
// store calls these; the components never compute any of it themselves.
// Same no-Vue/no-Firebase discipline as geometry.js.
import { BLACK } from './geometry';

export const key = (r, c) => `${r}-${c}`;
export const parseKey = (k) => k.split('-').map(Number);

export const isWhite = (puzzle, r, c) => (
  r >= 0 && c >= 0 && r < puzzle.size && c < puzzle.size && puzzle.grid[r][c] !== BLACK
);

// Per puzzle object, worked out once: a Giant has ~8,000 white squares and
// solvedness and fullness are checked on every keystroke. Callers must not
// mutate the list.
const whiteCache = new WeakMap();

/** Every white cell of the grid as [r, c], row-major. */
export function whiteCells(puzzle) {
  const hit = whiteCache.get(puzzle);
  if (hit && hit.grid === puzzle.grid) return hit.cells;
  const out = [];
  for (let r = 0; r < puzzle.size; r += 1) {
    for (let c = 0; c < puzzle.size; c += 1) if (isWhite(puzzle, r, c)) out.push([r, c]);
  }
  whiteCache.set(puzzle, { grid: puzzle.grid, cells: out });
  return out;
}

/** The clue (across or down entry) covering a cell, or null for a cell in no word that way. */
export function clueAt(puzzle, r, c, dir) {
  const list = dir === 'across' ? puzzle.across : puzzle.down;
  return list.find((e) => (dir === 'across'
    ? e.row === r && c >= e.col && c < e.col + e.len
    : e.col === c && r >= e.row && r < e.row + e.len)) || null;
}

export function cellsOf(entry, dir) {
  const cells = [];
  for (let k = 0; k < entry.len; k += 1) {
    cells.push(dir === 'across' ? [entry.row, entry.col + k] : [entry.row + k, entry.col]);
  }
  return cells;
}

/** The letter in a cell: progress letters are { l } objects or plain strings. */
export const letterAt = (letters, r, c) => {
  const v = letters[key(r, c)];
  if (!v) return '';
  return typeof v === 'string' ? v : (v.l || '');
};

/** A direction a cell actually has a word in; prefers `want`. */
export function usableDir(puzzle, r, c, want) {
  if (clueAt(puzzle, r, c, want)) return want;
  const other = want === 'across' ? 'down' : 'across';
  return clueAt(puzzle, r, c, other) ? other : want;
}

/** The next white cell in reading order for a direction, wrapping around. */
export function stepCell(puzzle, r, c, dir, delta) {
  const n = puzzle.size;
  let [rr, cc] = [r, c];
  for (let guard = 0; guard < n * n; guard += 1) {
    if (dir === 'across') {
      cc += delta;
      if (cc >= n) { cc = 0; rr += 1; }
      if (cc < 0) { cc = n - 1; rr -= 1; }
      if (rr >= n) rr = 0;
      if (rr < 0) rr = n - 1;
    } else {
      rr += delta;
      if (rr >= n) { rr = 0; cc += 1; }
      if (rr < 0) { rr = n - 1; cc -= 1; }
      if (cc >= n) cc = 0;
      if (cc < 0) cc = n - 1;
    }
    if (isWhite(puzzle, rr, cc)) return [rr, cc];
  }
  return [r, c];
}

/**
 * After typing in (r, c): the next cell of the same word, or - at the end
 * of the word - the first empty cell of the next clue (the NYT feel).
 * With `jump: false` the end of the word is where the cursor stays (Seth,
 * 2026-10-09: some solvers would rather move on themselves). With `wrap`
 * (the default), a word with empty squares only before the cursor sends it
 * back to the first of them: RACE can go in as E, R, A, C (Seth, 2026-10-09).
 */
export function advanceAfterType(puzzle, letters, r, c, dir, { jump = true, wrap = true } = {}) {
  const entry = clueAt(puzzle, r, c, dir);
  if (entry) {
    const cells = cellsOf(entry, dir);
    const idx = cells.findIndex(([rr, cc]) => rr === r && cc === c);
    for (let k = idx + 1; k < cells.length; k += 1) {
      const [rr, cc] = cells[k];
      if (!letterAt(letters, rr, cc)) return { row: rr, col: cc, dir };
    }
    if (wrap) {
      const empty = cells.slice(0, idx).find(([rr, cc]) => !letterAt(letters, rr, cc));
      if (empty) return { row: empty[0], col: empty[1], dir };
    }
    if (idx + 1 < cells.length) return { row: cells[idx + 1][0], col: cells[idx + 1][1], dir };
  }
  if (!jump) return { row: r, col: c, dir };
  return nextClue(puzzle, letters, r, c, dir, 1, { preferEmpty: true });
}

/** The previous cell of the word (for backspace), staying in the word. */
export function retreat(puzzle, r, c, dir) {
  const entry = clueAt(puzzle, r, c, dir);
  if (!entry) return { row: r, col: c, dir };
  const cells = cellsOf(entry, dir);
  const idx = cells.findIndex(([rr, cc]) => rr === r && cc === c);
  if (idx <= 0) return { row: r, col: c, dir };
  return { row: cells[idx - 1][0], col: cells[idx - 1][1], dir };
}

/**
 * Jump to the next (delta 1) or previous (-1) clue in the direction's list,
 * continuing into the other direction's list at the end, the way every
 * crossword app's clue arrows behave. Lands on the first empty cell of
 * that clue, or its first cell when it is full (Matt, 2026-10-15). With
 * `preferEmpty` (typing past a word) full clues are skipped; the arrows
 * still stop at them, so a finished word can be checked.
 */
export function nextClue(puzzle, letters, r, c, dir, delta, { preferEmpty = false } = {}) {
  const order = [
    ...puzzle.across.map((e) => ({ e, dir: 'across' })),
    ...puzzle.down.map((e) => ({ e, dir: 'down' })),
  ];
  const current = clueAt(puzzle, r, c, dir);
  let idx = order.findIndex((o) => o.dir === dir && o.e === current);
  if (idx === -1) idx = 0;
  for (let step = 1; step <= order.length; step += 1) {
    const o = order[(idx + delta * step + order.length * step) % order.length];
    const cells = cellsOf(o.e, o.dir);
    const empty = cells.find(([rr, cc]) => !letterAt(letters, rr, cc));
    if (preferEmpty && !empty) continue; // a full word: skip it when hunting for work
    const [rr, cc] = empty || cells[0];
    return { row: rr, col: cc, dir: o.dir };
  }
  // Everything is full: just go to the next clue.
  const o = order[(idx + delta + order.length) % order.length];
  const [rr, cc] = cellsOf(o.e, o.dir)[0];
  return { row: rr, col: cc, dir: o.dir };
}

export const answerAt = (puzzle, r, c) => puzzle.grid[r][c];

export function isSolved(puzzle, letters) {
  return whiteCells(puzzle).every(([r, c]) => letterAt(letters, r, c) === answerAt(puzzle, r, c));
}

export function filledCount(puzzle, letters) {
  return whiteCells(puzzle).filter(([r, c]) => letterAt(letters, r, c)).length;
}

/**
 * The "full but wrong" nudge (Seth, 2026-10-09: "When I finish and I'm not
 * right nothing happens"). Fires once, on the change that fills the last
 * empty square of an unsolved grid; emptying a square re-arms it. `initial`
 * is a load (resume, joining a room): it records fullness but never fires,
 * so opening a grid that is already full and wrong stays quiet.
 */
export function fullNoticeStep({ wasFull, full, solved, initial = false }) {
  if (!full) return { wasFull: false, show: false };
  return { wasFull: true, show: !wasFull && !solved && !initial };
}

/** Of the given cells, those holding a letter that is wrong. */
export function wrongCells(puzzle, letters, cells) {
  return cells.filter(([r, c]) => {
    const l = letterAt(letters, r, c);
    return l && l !== answerAt(puzzle, r, c);
  });
}

// The clock only counts while someone is solving (Matt 2026-10-27: "the
// timers keep going ... even when nobody's actually working on the puzzle").
// Two minutes with no tap or key and it stops, and those two idle minutes
// come back off: the time stands where it was at the last touch.
export const IDLE_MS = 2 * 60 * 1000;
// A room's `activeAt` is refreshed at most this often while it runs.
export const ACTIVE_WRITE_MS = 15 * 1000;

// A room's clock lives in its meta as `banked` (seconds already counted),
// `runFrom` (when the current stretch began) and `activeAt` (anyone's last
// tap or key). It runs while `activeAt` is under IDLE_MS old and otherwise
// reads as stopped at `activeAt` - so nobody has to write "paused", and a
// phone that drops off can't keep it going. Rooms from before this have
// only `startedAt` and read as they always did until someone touches them.
const legacyRoom = (meta) => meta?.runFrom == null;

/** Whether a room's clock is stopped for idleness at `at`. */
export const roomPaused = (meta, at) => Boolean(meta) && !legacyRoom(meta)
  && at - (meta.activeAt || 0) > IDLE_MS;

/** A room's shared clock read at `at`: whole seconds of solving. */
export function roomSeconds(meta, at) {
  if (!meta) return 0;
  if (legacyRoom(meta)) {
    return meta.startedAt ? Math.max(0, Math.floor((at - meta.startedAt) / 1000)) : 0;
  }
  const end = roomPaused(meta, at) ? meta.activeAt : at;
  return Math.max(0, Math.floor((meta.banked || 0) + Math.max(0, end - meta.runFrom) / 1000));
}

/** A fresh room's clock fields, starting from `seconds` already spent. */
export const roomClockStart = (seconds, at) => ({ banked: seconds || 0, runFrom: at, activeAt: at });

/**
 * What a tap or key at `at` writes to the room's meta: a stopped (or
 * pre-pause) clock starts a new stretch from where it stood; a running one
 * just refreshes `activeAt` now and then. Null when nothing needs writing.
 */
export function roomClockPatch(meta, at) {
  if (!meta || meta.solvedAt) return null;
  if (legacyRoom(meta) || roomPaused(meta, at)) return roomClockStart(roomSeconds(meta, at), at);
  if (at - (meta.activeAt || 0) >= ACTIVE_WRITE_MS) return { activeAt: at };
  return null;
}

export function formatTime(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/** The on-screen keyboard. '⌫' is backspace and '⇄' flips direction. */
export const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['⇄', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫'],
];

/** Colours for people solving together, by join order. */
export const SOLVER_COLORS = ['#f0b64b', '#5bb8d4', '#e05f7a', '#7fc45c', '#b98cf0', '#ff8a5b'];
